import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Disc3, Building2, LayoutDashboard, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardTarget } from '@/hooks/useDashboardTarget';

// Conversational self-segmentation band. Four intent paths route the visitor to
// the right surface. Routing is auth-aware but purely client-side — no backend,
// no stored personalization (keeps the V1 "no complex personalization" guardrail).
export function WhatAreYouHereFor() {
  const { isAuthenticated, user } = useAuth();
  const dashboardTo = useDashboardTarget();

  const isDj = user?.user_type === 'dj';

  const paths = [
    { label: 'I want to attend events', icon: Ticket, to: '/events' },
    {
      label: "I'm a DJ",
      icon: Disc3,
      to: !isAuthenticated ? '/login' : (isDj ? '/dj-dashboard' : '/dj-application'),
    },
    {
      label: "I'm an organizer",
      icon: Building2,
      to: isAuthenticated ? '/organizer/events' : '/login',
    },
    { label: "I'm returning to my dashboard", icon: LayoutDashboard, to: dashboardTo },
  ];

  return (
    <section className="py-16 bg-muted/30 animate-fade-in">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-2">
            What are you <span className="text-gradient-wolf">here for?</span>
          </h2>
          <p className="text-muted-foreground">Pick a path — we'll take you straight there</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {paths.map((p) => {
            const Icon = p.icon;
            return (
              <Link key={p.label} to={p.to} className="group">
                <div className="glass-card hover-lift rounded-xl p-6 border border-border/50 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="flex-1 font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    {p.label}
                  </span>
                  <ArrowRight className="w-5 h-5 text-primary transition-transform group-hover:translate-x-1 flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default WhatAreYouHereFor;
